import React from 'react';
import { Clock, ArrowRight, Mail } from 'lucide-react';

interface DemoRateLimitedProps {
  message: string;
  retryAfter: string;
  onContactClick: () => void;
}

export function DemoRateLimited({ message, retryAfter, onContactClick }: DemoRateLimitedProps) {
  // Format the retry time
  const retryDate = new Date(retryAfter);
  const now = new Date();
  const hoursUntilRetry = Math.max(0, Math.ceil((retryDate.getTime() - now.getTime()) / (1000 * 60 * 60)));

  const formatRetryTime = () => {
    if (hoursUntilRetry <= 0) {
      return 'shortly';
    } else if (hoursUntilRetry === 1) {
      return 'in about 1 hour';
    } else if (hoursUntilRetry < 24) {
      return `in about ${hoursUntilRetry} hours`;
    } else {
      return retryDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm text-center">
      {/* Icon */}
      <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center">
        <Clock className="w-8 h-8 text-amber-600" />
      </div>

      {/* Heading */}
      <h3 className="text-2xl font-bold text-gsp-text-primary mb-2">
        Demo Limit Reached
      </h3>
      <p className="text-gsp-text-body mb-6 max-w-md mx-auto">
        {message}
      </p>

      {/* Retry Time */}
      <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 rounded-lg mb-8">
        <Clock className="w-4 h-4 text-amber-600" />
        <span className="text-sm text-amber-700">
          You can try again <strong>{formatRetryTime()}</strong>
        </span>
      </div>

      {/* CTA Section */}
      <div className="bg-gsp-teal/5 rounded-lg p-6 mb-6">
        <h4 className="font-semibold text-gsp-text-primary mb-2">
          Need to analyze more documents?
        </h4>
        <p className="text-sm text-gsp-text-body mb-4">
          Contact us to learn about our full platform with unlimited analysis capabilities.
        </p>
        <button
          onClick={onContactClick}
          className="inline-flex items-center gap-2 px-6 py-3 bg-gsp-orange text-white rounded-lg font-semibold hover:bg-gsp-orange/90 transition-colors"
        >
          Get in Touch
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* Direct Contact */}
      <p className="text-sm text-gsp-text-body flex items-center justify-center gap-2">
        <Mail className="w-4 h-4" />
        Or email us directly at{' '}
        <a href="mailto:info@propvetter.com" className="text-gsp-teal hover:underline">
          info@propvetter.com
        </a>
      </p>
    </div>
  );
}

export default DemoRateLimited;
